/**
 * "Add another person" dialog — captures a new elder's name and calls the
 * create_account callable, then navigates into the fresh account. Opened from
 * the EldersDropdown switcher. Unlike first-signup (which scaffolds an empty
 * account and routes to /welcome to name it), we take the name up front here:
 * the caregiver is already oriented, and a name-less second account would trip
 * RootRedirect's "first sign-in" branch.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { HealthConsentCheckbox } from '@/components/HealthConsentCheckbox';
import { createAccount } from '@/lib/callables';

export function AddPersonDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [healthConsent, setHealthConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function onClose(next: boolean) {
    if (busy) return; // don't let a backdrop click cancel an in-flight create
    if (!next) {
      setName('');
      setHealthConsent(false);
      setError(null);
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogTitle>Add another person</DialogTitle>
        <DialogDescription className="mt-1">
          Set up a separate space for someone else you care for. You'll be their
          admin; their calendar, voice, and buttons are all their own.
        </DialogDescription>
        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const elderName = name.trim();
            if (!elderName || !healthConsent) return;
            setBusy(true);
            setError(null);
            try {
              const { data } = await createAccount({ elderName, healthConsent });
              setName('');
              setHealthConsent(false);
              onOpenChange(false);
              navigate(`/accounts/${data.accountId}/voice`);
            } catch (err) {
              setError(
                err instanceof Error
                  ? err.message
                  : "Couldn't add this person. Please try again.",
              );
              setBusy(false);
            }
          }}
        >
          <Field
            label="Their name"
            htmlFor="add-person-name"
            hint="e.g. Mum, or Margaret — this is how they'll appear in your list."
          >
            <Input
              id="add-person-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              maxLength={100}
              placeholder="e.g. Margaret"
              autoFocus
            />
          </Field>
          <HealthConsentCheckbox
            checked={healthConsent}
            onCheckedChange={setHealthConsent}
            idPrefix="add-person"
          />
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onClose(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !name.trim() || !healthConsent}>
              {busy ? 'Adding…' : 'Add person'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
